import type {
  ArgMapping, Block, BlockType, BulletListBlock, ButtonSpec, CardSpec,
  EmbedFilter, FeatureItem, GalleryItem, HeroSlide, ListItem, LogoItem, NumberedListBlock,
  PricingTier, TimelineItem, TodoBlock, TodoItem,
} from './types'

let _idCounter = 0
export function newId(prefix = 'b'): string {
  _idCounter += 1
  return `${prefix}_${Date.now().toString(36)}_${_idCounter}`
}

export function newFilterId(): string { return newId('f') }
export function newButtonId(): string { return newId('btn') }

export function makeButton(label = 'Get started', variant: ButtonSpec['variant'] = 'primary'): ButtonSpec {
  return { id: newButtonId(), label, url: '#', variant, size: 'md' }
}

export function makeFeatureItem(): FeatureItem {
  return { id: newId('feat'), icon: '⚡', title: 'Feature', description: 'Describe what makes this feature great.' }
}

export function makeTimelineItem(date = '', title = 'Milestone', description = '', icon = '✨'): TimelineItem {
  return { id: newId('tl'), date, title, description, icon }
}

export function makeGalleryItem(): GalleryItem {
  return { id: newId('img'), url: 'https://images.unsplash.com/photo-1497436072909-f5e4be1713a0?w=900', caption: '' }
}

export function makeHeroSlide(): HeroSlide {
  return { id: newId('slide'), type: 'image', url: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600' }
}

export function makeLogoItem(): LogoItem {
  return { id: newId('logo'), name: 'Brand', url: '' }
}

export function makeTodoItem(text = '', checked = false): TodoItem {
  return { id: newId('todo'), text, checked }
}

export function makeListItem(text = '', indent = 0): ListItem {
  return { id: newId('li'), text, indent }
}

// Migrate a legacy bullet/numbered block (`{ text, indent }`) into the
// multi-item shape (`{ items: [...] }`). No-op for already-migrated
// blocks.
export function migrateListBlock<T extends BulletListBlock | NumberedListBlock>(b: any): T {
  if (b && typeof b === 'object' && Array.isArray(b.items)) {
    return b as T
  }
  const text: string = typeof b?.text === 'string' ? b.text : ''
  const indent: number = typeof b?.indent === 'number' ? b.indent : 0
  const { text: _t, indent: _i, ...rest } = b ?? {}
  return { ...rest, items: [makeListItem(text, indent)] } as T
}

// Migrate a legacy todo block (`{ text, checked }`) into the v2 list shape
// (`{ items: [...] }`). No-op for already-migrated blocks. We keep this in
// `blocks.ts` so both the loader and any in-memory normalisation paths use
// the same conversion.
export function migrateTodoBlock(b: any): TodoBlock {
  if (b && typeof b === 'object' && Array.isArray(b.items)) {
    return b as TodoBlock
  }
  const text: string = typeof b?.text === 'string' ? b.text : ''
  const checked: boolean = Boolean(b?.checked)
  const { text: _t, checked: _c, ...rest } = b ?? {}
  return { ...rest, type: 'todo', items: [makeTodoItem(text, checked)] }
}

export function makeCard(): CardSpec {
  return {
    id: newId('card'),
    title: 'Card title',
    subtitle: 'Subtitle',
    body: 'A short description that explains the value of this card.',
    imageUrl: '',
    cta: makeButton('Learn more', 'link'),
  }
}

export function makePricingTier(name = 'Starter', price = '$9', highlighted = false): PricingTier {
  return {
    id: newId('tier'),
    name,
    price,
    period: '/mo',
    description: 'Best for individuals getting started.',
    features: ['Up to 3 projects', 'Email support', 'Basic analytics'],
    cta: makeButton(highlighted ? 'Start free trial' : 'Choose plan', highlighted ? 'primary' : 'outline'),
    highlighted,
  }
}

// Creates a fresh block. When `presetId` is provided (typically a
// human-readable id like `heading_3` from `nextBlockIdForType`), it is used
// as the block's id; otherwise a unique fallback id is generated. Callers
// that build blocks for a specific page should pass the preset id so block
// ids stay short, predictable, and aligned with the user-facing format.
//
// `ctx.pageName` is the identifier (obj_name) of the page the block is being
// added to. It seeds the default DOCUMENT TYPE on `file_upload` blocks so a
// freshly-dropped doc/image block is usable right away — without it the
// upload control stays disabled until the author fills in document type +
// transaction id. Callers that add blocks to a known page should pass it.
export function makeBlock(
  type: BlockType,
  presetId?: string,
  ctx?: { pageName?: string },
): Block {
  const id = presetId ?? newId()
  switch (type) {
    // text
    case 'heading':       return { id, type, level: 2, text: '' }
    case 'paragraph':     return { id, type, text: '' }
    case 'bullet_list':   return { id, type, items: [makeListItem()] }
    case 'numbered_list': return { id, type, items: [makeListItem()] }
    case 'todo':          return { id, type, items: [makeTodoItem()] }
    case 'quote':         return { id, type, text: '' }
    case 'code':          return { id, type, text: '', language: 'text' }
    case 'divider':       return { id, type }

    // media
    case 'image':         return { id, type, url: '', alt: '', caption: '', rounded: true, shadow: false, align: 'center' }
    case 'video':         return { id, type, provider: 'youtube', url: '', aspect: '16:9' }
    case 'gallery':       return {
      id, type, columns: 3, rounded: true, gap: 12,
      items: [makeGalleryItem(), makeGalleryItem(), makeGalleryItem()],
    }
    case 'logo_cloud':    return {
      id, type, title: 'Trusted by teams everywhere',
      monochrome: true,
      items: [makeLogoItem(), makeLogoItem(), makeLogoItem(), makeLogoItem(), makeLogoItem()],
    }
    case 'map':           return {
      id, type,
      provider: 'leaflet',
      locationMode: 'address',
      address: '',
      latitude: 37.7749,
      longitude: -122.4194,
      zoom: 14,
      height: 400,
    }

    // layout / sections
    case 'hero':          return {
      id, type,
      eyebrow: 'New release',
      title: 'Build beautiful pages in minutes',
      subtitle: 'A flexible, fast page builder for your team. Drop in components and ship faster.',
      align: 'center',
      height: 'normal',
      // Theme-driven backdrop. The CSS variables are resolved at paint
      // time, so the hero re-colors itself whenever the active theme
      // changes — no per-block edit needed. `--pg-on-primary` is the
      // contrast-safe text color the theme guarantees against the
      // brand band (white on dark primaries, near-black on light ones).
      background: { kind: 'gradient', gradientFrom: 'var(--pg-primary)', gradientTo: 'var(--pg-accent)', gradientAngle: 135 },
      textColor: 'var(--pg-on-primary)',
      primaryCta: makeButton('Get started', 'primary'),
      secondaryCta: makeButton('Learn more', 'outline'),
      imageSide: 'none',
    }
    case 'cta_banner':    return {
      id, type,
      title: 'Ready to dive in?',
      subtitle: 'Start your free 14-day trial — no credit card required.',
      align: 'center',
      background: { kind: 'color', color: 'var(--pg-primary)' },
      textColor: 'var(--pg-on-primary)',
      primaryCta: makeButton('Get started', 'secondary'),
      secondaryCta: makeButton('Talk to sales', 'ghost'),
    }
    case 'spacer':        return { id, type, height: 48 }
    case 'columns':       return {
      id, type, count: 2, gap: 24, align: 'top',
      columns: [
        { id: newId('col'), text: 'First column content. Click to edit.', blocks: [] },
        { id: newId('col'), text: 'Second column content. Click to edit.', blocks: [] },
      ],
    }
    case 'container':     return {
      id, type,
      // Theme-aware surface — `--pg-surface-alt` steps up from the page
      // background by a small amount so the container reads as a
      // distinct region without the user picking a color.
      background: { kind: 'color', color: 'var(--pg-surface-alt)' },
      padding: 24, rounded: true, align: 'left',
      text: 'A container with custom background. Use this to call out a region of your page.',
    }
    case 'card':          return { id, type, card: makeCard() }
    case 'card_grid':     return {
      id, type, columns: 3, gap: 16,
      cards: [makeCard(), makeCard(), makeCard()],
    }

    // content sections
    case 'feature_grid':  return {
      id, type, columns: 3, align: 'left', iconStyle: 'circle',
      eyebrow: 'Why us', heading: 'Everything you need',
      subheading: 'A comprehensive toolkit to help your team move faster.',
      items: [makeFeatureItem(), makeFeatureItem(), makeFeatureItem()],
    }
    case 'stats':         return {
      id, type, columns: 4, align: 'center',
      heading: 'Trusted by teams worldwide',
      items: [
        { id: newId('stat'), value: '99.9', suffix: '%', label: 'Uptime', description: 'Across all regions' },
        { id: newId('stat'), value: '12k', suffix: '+', label: 'Customers', description: 'And growing' },
        { id: newId('stat'), value: '4.9', suffix: '★', label: 'Avg rating' },
        { id: newId('stat'), value: '24', suffix: '/7', label: 'Support' },
      ],
    }
    case 'countdown':     return {
      id, type, columns: 4, align: 'center',
      heading: 'Countdown',
      // Default target: ~30 days out so a freshly-dropped block ticks
      // immediately instead of showing the expired state.
      target: new Date(Date.now() + 30 * 86400_000).toISOString(),
      showSeconds: true,
      expiredText: "It's here!",
    }
    // A live clock since a start time. Inside a repeat, point `from` at the
    // row (`{{item.CREATED_AT}}`) and the thresholds at its columns.
    case 'timer':         return {
      id, type, from: '{{item.CREATED_AT}}', format: 'auto', textSize: 'md',
      label: 'Waiting', warnAfter: '10', dangerAfter: '20',
    }
    case 'testimonial':   return {
      id, type,
      quote: 'This tool transformed the way our team ships. We move faster and design better.',
      authorName: 'Jane Doe',
      authorRole: 'Head of Product, Acme',
      authorAvatarUrl: '',
      rating: 5, align: 'center',
    }
    case 'pricing':       return {
      id, type, columns: 3,
      heading: 'Simple, transparent pricing',
      subheading: 'Start free. Pay only when you need more.',
      tiers: [
        makePricingTier('Starter', '$0'),
        { ...makePricingTier('Pro', '$29', true), badge: 'Most popular' },
        makePricingTier('Team', '$99'),
      ],
    }
    case 'faq':           return {
      id, type, heading: 'Frequently asked questions',
      items: [
        { id: newId('faq'), question: 'How does it work?', answer: 'Explain it briefly here.' },
        { id: newId('faq'), question: 'Can I cancel anytime?', answer: 'Yes — no commitments.' },
        { id: newId('faq'), question: 'Do you offer support?', answer: 'Email support is included on all plans.' },
      ],
    }
    case 'tabs':          return {
      id, type, variant: 'underline',
      items: [
        { id: newId('tab'), label: 'Overview', body: 'Tab 1 content goes here.', blocks: [] },
        { id: newId('tab'), label: 'Features', body: 'Tab 2 content goes here.', blocks: [] },
        { id: newId('tab'), label: 'Pricing',  body: 'Tab 3 content goes here.', blocks: [] },
      ],
    }
    case 'accordion':     return {
      id, type,
      items: [
        { id: newId('acc'), title: 'Section 1', body: 'Body for section 1.', defaultOpen: true, blocks: [] },
        { id: newId('acc'), title: 'Section 2', body: 'Body for section 2.', blocks: [] },
        { id: newId('acc'), title: 'Section 3', body: 'Body for section 3.', blocks: [] },
      ],
    }
    case 'callout':       return {
      id, type, variant: 'info',
      title: 'Heads up', body: 'This is an informational callout. Edit me in the property panel.',
      icon: '💡',
    }
    case 'timeline':      return {
      id, type,
      eyebrow: 'Our journey',
      heading: 'Milestones along the way',
      subheading: 'A snapshot of how we got here — and where we’re heading next.',
      layout: 'vertical',
      markerStyle: 'icon',
      align: 'left',
      items: [
        makeTimelineItem('2022', 'Founded', 'Three founders, one shared frustration with brittle internal tools.', '🚀'),
        makeTimelineItem('2023', 'First customers', 'Shipped the MVP and onboarded ten teams in the first month.', '🌱'),
        makeTimelineItem('2024', 'Scale', 'Crossed a thousand active workspaces and opened the EU region.', '⚡'),
        makeTimelineItem('Today', 'What’s next', 'AI-assisted authoring, multiplayer editing, and full-stack codegen.', '🌟'),
      ],
    }

    // action
    case 'button':        return { id, type, button: makeButton('Click me', 'primary'), align: 'left' }
    case 'button_group':  return {
      id, type, align: 'left', gap: 8,
      buttons: [makeButton('Primary', 'primary'), makeButton('Secondary', 'outline')],
    }

    // form
    case 'newsletter':    return {
      id, type,
      heading: 'Subscribe to our newsletter',
      subheading: 'Get the latest updates in your inbox.',
      placeholder: 'you@example.com',
      buttonLabel: 'Subscribe',
      align: 'center',
      background: { kind: 'color', color: 'var(--pg-surface-alt)' },
    }
    case 'contact_form':  return {
      id, type,
      heading: 'Get in touch',
      subheading: 'We usually reply within one business day.',
      buttonLabel: 'Send message',
      align: 'left',
      fields: [
        { id: newId('fld'), name: 'name',    label: 'Name',    type: 'text',     placeholder: 'Your name', required: true },
        { id: newId('fld'), name: 'email',   label: 'Email',   type: 'email',    placeholder: 'you@example.com', required: true },
        { id: newId('fld'), name: 'message', label: 'Message', type: 'textarea', placeholder: 'How can we help?', required: true },
      ],
    }

    // app
    case 'input_group':   return { id, type, inputs: [], layout: 'inline' }
    case 'embed':         return {
      id, type,
      embed: { obj_name: '', obj_type: 'T', mode: 'list', filters: [], renderMode: 'realtime' },
    }
    case 'file_upload':   return {
      id, type,
      kind: 'document',
      // Seed sensible defaults so the upload control is enabled the moment the
      // block is dropped (an empty docType/transactionId disables it). The
      // document type groups uploads by page (its obj_name); the transaction
      // id is the block's own id, so each upload block gets its own record
      // folder under `<docType>/<transactionId>/`. Both are fixed values the
      // author can swap for a page variable in the block editor.
      docType: { source: 'static', value: ctx?.pageName || '' },
      transactionId: { source: 'static', value: id },
      label: 'Attach a file',
    }

    // v2 additions
    case 'form':          return {
      id, type,
      heading: 'Get in touch',
      subheading: 'We will reply within one business day.',
      submitLabel: 'Submit',
      layout: 'stacked',
      align: 'left',
      fields: [
        { id: newId('fld'), name: 'name',    label: 'Name',    type: 'text',     placeholder: 'Your name',       required: true },
        { id: newId('fld'), name: 'email',   label: 'Email',   type: 'email',    placeholder: 'you@example.com', required: true },
        { id: newId('fld'), name: 'message', label: 'Message', type: 'textarea', placeholder: 'How can we help?' },
      ],
    }
    case 'custom_code':   return {
      id, type,
      sandbox: true,
      html: '<div class="hello">Hi from custom code</div>',
      css:  '.hello { padding: 16px; color: #2563eb; font-weight: 600; }',
      js:   '',
    }
    case 'custom_html':   return {
      id, type,
      sandbox: true,
      html: '<div style="padding:16px;text-align:center;font-weight:600">Your custom HTML here</div>',
      css:  '',
      js:   '',
    }
    case 'nav_menu':      return { id, type, source: 'site', align: 'left', includeHome: true }
    case 'cart_badge':    return { id, type, label: 'Cart', href: '/cart', icon: 'shopping-cart', hideWhenEmpty: true, variant: 'primary' }
    case 'cart_lines':    return {
      id, type, editable: true,
      emptyText: 'Your cart is empty.',
      continueLabel: 'Continue browsing', continueHref: '/',
    }
    case 'wishlist_button': return {
      id, type, sku: '', name: '', addLabel: 'Add to wishlist', removeLabel: 'Saved to wishlist',
      display: 'button', variant: 'outline',
    }
    case 'wishlist_badge':  return { id, type, label: 'Wishlist', href: '/wishlist', icon: 'heart', hideWhenEmpty: true, variant: 'outline' }
    case 'wishlist_lines':  return {
      id, type, showMove: true,
      emptyText: 'Your wishlist is empty.',
      continueLabel: 'Continue browsing', continueHref: '/',
    }
    case 'cart_summary':  return {
      id, type, heading: 'Order summary',
      subtotalLabel: 'Subtotal', totalLabel: 'Total',
      checkoutLabel: 'Proceed to checkout', checkoutHref: '/checkout',
    }
    case 'filter_bar':    return {
      id, type, target: '', layout: 'sidebar',
      search: { label: 'Search', placeholder: 'Search' },
      facets: [{ id: newId('ff'), label: 'Category', from: 'eyebrow', showCounts: true }],
      sort: [
        { id: newId('fs'), label: 'Price, low to high', by: 'price', dir: 'asc' },
        { id: newId('fs'), label: 'Price, high to low', by: 'price', dir: 'desc' },
        { id: newId('fs'), label: 'Name, A–Z', by: 'title', dir: 'asc' },
      ],
      resultLabel: 'Showing {n}',
      emptyText: 'Nothing matches that.',
    }
    case 'breadcrumb':    return {
      id, type, separator: '/', align: 'left', structuredData: true,
      items: [
        { id: newId('bc'), label: 'Home', href: '/' },
        { id: newId('bc'), label: 'This page' },
      ],
    }
    case 'variable':      return {
      id, type,
      name: '',
      label: '',
      format: 'auto',
      textSize: 'md',
      align: 'left',
      emptyText: '—',
    }
    case 'notepad':       return {
      id, type,
      html: '',
      minHeight: 160,
    }
    case 'collection_search': return {
      id, type,
      collection: '',
      placeholder: 'Search…',
      limit: 12,
      emptyText: 'Nothing matches that search.',
    }
    case 'comments': return {
      id, type,
      collection: '',
      recordSlug: '{{item._slug}}',
      heading: 'Comments',
      formHeading: 'Leave a comment',
      emptyText: 'No comments yet — be the first.',
    }
    case 'rich_content':  return {
      id, type,
      source: '{{item.body}}',
      toc: 'auto',
      typography: 'article',
      readableWidth: true,
      externalLinksNewTab: true,
      emptyText: '',
    }
    case 'chatbot':       return {
      id, type,
      title: 'Assistant',
      greeting: 'Hi! Ask me anything about this page or your data.',
      groundOnPage: true,
      dataSources: [],
      suggestedQuestions: [],
      height: 420,
    }
    case 'auth_form':     return {
      id, type,
      mode: 'login',
      heading: 'Sign in',
      subheading: '',
      submitLabel: 'Sign in',
      showTwasta: true,
      showRegisterLink: true,
    }
    case 'auth_menu':     return {
      id, type,
      loginLabel: 'Log in',
      signupLabel: 'Sign up',
      showSignup: true,
      align: 'right',
      links: [],
      showProfile: true,
      showChangePassword: true,
      allowAvatarUpload: true,
    }
    case 'table':         return {
      id, type,
      heading: 'Comparison',
      subheading: 'Side-by-side overview.',
      striped: true,
      bordered: false,
      compact: false,
      align: 'left',
      columns: [
        { id: newId('col'), label: 'Feature', align: 'left' },
        { id: newId('col'), label: 'Free',    align: 'center' },
        { id: newId('col'), label: 'Pro',     align: 'center' },
      ],
      rows: [
        ['Projects',   '1',  'Unlimited'],
        ['Team seats', '1',  '10'],
        ['Support',    'Email', 'Priority'],
      ],
    }
    // ── eCommerce ──
    case 'entity_picker':      return {
      id, type,
      source: { kind: 'api', endpointId: 'customers' },
      outputBindings: [{ ...makeArgMapping('value'), source: { source: 'page_input', name: 'customer' } }],
      toggleBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'isNewCustomer' } },
      showCreateNewToggle: true,
      searchPlaceholder: 'Search by phone or name',
      layout: 'spacious',
    }
    case 'new_customer_form':  return {
      id, type,
      source: { kind: 'api', endpointId: 'customers' },
      outputBindings: [{ ...makeArgMapping('value'), source: { source: 'page_input', name: 'customer' } }],
      toggleBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'isNewCustomer' } },
      layout: 'single-column',
      submitLabel: 'Save customer',
    }
    case 'product_selection':  return {
      id, type,
      source: { kind: 'api', endpointId: 'products' },
      cartBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'cart' } },
      layout: 'grid',
      columns: 3,
      catalogueMode: 'both',
      catalogueButtonLabel: 'Browse catalogue',
      catalogueTitle: 'Catalogue',
      catalogueColumns: 4,
      showCategorySidebar: true,
      showCartDrawer: true,
    }
    case 'product_preview':    return {
      id, type,
      attributesSource: 'product_selection_state',
      layout: 'card',
      aspect: '1:1',
      showCaption: true,
      showAttributeChips: true,
      emptyStateTitle: 'Live preview',
      emptyStateBody: 'Pick a product and customise — the preview updates as you select swatches, fits, and trims.',
    }
    case 'order_summary':      return {
      id, type,
      cartBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'cart' } },
      editable: true,
      showSubtotal: true,
    }
    case 'order_submit':       return {
      id, type,
      source: { kind: 'api', endpointId: 'sales_orders' },
      customerBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'customer' } },
      cartBinding: { ...makeArgMapping('value'), source: { source: 'page_input', name: 'cart' } },
      outputBindings: [
        { ...makeArgMapping('orderId'), label: 'Order id', source: { source: 'page_input', name: 'salesOrderId' } },
        { ...makeArgMapping('orderNumber'), label: 'Order number', source: { source: 'page_input', name: 'salesOrderNumber' } },
      ],
      buttonLabel: 'Place Order',
      successTitle: 'Order {{orderNumber}} created',
      showStartNew: true,
    }
    // Storefront commerce blocks. Objects and columns are deliberately blank:
    // they are picked from the real transaction models in the property panel.
    case 'cart_checkout':  return {
      id, type,
      order: { object: '', lines: { detailFormNo: '2', skuColumn: '', qtyColumn: '' } },
      fields: [
        { key: 'name', label: 'Full name', required: true, prefill: 'name', group: 'contact', autocomplete: 'name' },
        { key: 'email', label: 'Email', kind: 'email', required: true, prefill: 'email', group: 'contact', autocomplete: 'email' },
        { key: 'phone', label: 'Mobile', kind: 'tel', required: true, group: 'contact', autocomplete: 'tel' },
        { key: 'line1', label: 'Address', required: true, group: 'address', autocomplete: 'address-line1' },
        { key: 'city', label: 'City', required: true, group: 'address', autocomplete: 'address-level2' },
        { key: 'pin', label: 'PIN code', kind: 'postcode', required: true, group: 'address', autocomplete: 'postal-code' },
      ],
      orderIdVar: 'orderId',
      requireSignIn: true,
      showSummary: true,
      heading: 'Checkout',
      submitLabel: 'Place order',
    }
    case 'my_records':  return {
      id, type,
      object: '',
      columns: { reference: '', date: '' },
      heading: 'My records',
      pageSize: 10,
    }
    case 'saved_records':  return {
      id, type,
      object: '',
      kind: 'address',
      fields: [],
      defaultColumn: '',
      heading: '',
    }
    case 'reviews':  return {
      id, type,
      object: '',
      record: '',
      columns: { record: '', rating: '', body: '', status: '' },
      approvedValue: 'APPROVED',
      pendingValue: 'PENDING',
      submit: 'signed_in',
      heading: 'Reviews',
    }
    case 'payment_settings':   return {
      id, type,
      defaultProvider: 'razorpay',
      heading: 'Payment settings',
    }
    case 'payment_checkout':   return {
      id, type,
      // Deliberately blank: this block charges real money against a real
      // record, and a plausible-looking default column name would be a
      // guess the author might never correct.
      objName: '',
      txnIdVar: 'salesOrderId',
      amountColumn: '',
      writeback: { statusColumn: '', statusValue: 'PAID', paymentIdColumn: '' },
      buttonLabel: 'Pay now',
      heading: 'Payment',
      successTitle: 'Payment received',
      showCodOption: false,
      codLabel: 'Cash on delivery',
    }

    // ── Marketing sections ──
    // Each ships with real placeholder content rather than an empty shell:
    // a freshly-dropped block should look like the thing it is, so the author
    // can judge the layout and edit in place instead of staring at a stub.
    case 'bento_grid':         return {
      id, type, columns: 4, gap: 16,
      eyebrow: 'Platform',
      heading: 'Everything, in one place',
      cells: [
        { id: newId('bc'), span: 2, rowSpan: 2, icon: 'zap', title: 'Fast by default', body: 'Ship in minutes, not weeks.' },
        { id: newId('bc'), span: 2, icon: 'shield', title: 'Secure', body: 'Role-based access on every object.' },
        { id: newId('bc'), span: 1, icon: 'bar-chart', title: 'Analytics' },
        { id: newId('bc'), span: 1, icon: 'users', title: 'Collaborative' },
        { id: newId('bc'), span: 2, icon: 'globe', title: 'Global', body: 'Deploy anywhere your customers are.' },
      ],
    }
    case 'marquee':            return {
      id, type, speed: 30, direction: 'left', pauseOnHover: true, fadeEdges: true,
      separator: '•',
      items: [
        { id: newId('mq'), text: 'Ship faster' },
        { id: newId('mq'), text: 'Stay secure' },
        { id: newId('mq'), text: 'Scale globally' },
        { id: newId('mq'), text: 'Delight customers' },
      ],
    }
    case 'counter':            return {
      id, type, columns: 4, align: 'center', duration: 1600, separator: true,
      heading: 'By the numbers',
      items: [
        { id: newId('cnt'), value: 12000, suffix: '+', label: 'Customers' },
        { id: newId('cnt'), value: 99.9, decimals: 1, suffix: '%', label: 'Uptime' },
        { id: newId('cnt'), value: 40, prefix: '<', suffix: 'ms', label: 'Latency' },
        { id: newId('cnt'), value: 24, suffix: '/7', label: 'Support' },
      ],
    }
    case 'before_after':       return {
      id, type,
      beforeUrl: '', afterUrl: '',
      beforeLabel: 'Before', afterLabel: 'After',
      aspect: '16:9', initial: 50, orientation: 'horizontal',
    }
    case 'carousel':           return {
      id, type, height: 380, autoplay: true, autoplayInterval: 5000,
      showDots: true, showArrows: true, perView: 1,
      slides: [
        { id: newId('sl'), title: 'First slide', body: 'Describe what matters most.' },
        { id: newId('sl'), title: 'Second slide', body: 'Add another highlight here.' },
        { id: newId('sl'), title: 'Third slide', body: 'And one more for good measure.' },
      ],
    }
    case 'team':               return {
      id, type, columns: 4, shape: 'circle', align: 'left',
      eyebrow: 'Our team',
      heading: 'The people behind it',
      members: [
        { id: newId('tm'), name: 'Jane Doe', role: 'Founder & CEO' },
        { id: newId('tm'), name: 'Alex Kim', role: 'Head of Engineering' },
        { id: newId('tm'), name: 'Sam Patel', role: 'Design Lead' },
        { id: newId('tm'), name: 'Riya Shah', role: 'Customer Success' },
      ],
    }
    case 'steps':              return {
      id, type, layout: 'horizontal', numbered: true, align: 'left',
      eyebrow: 'How it works',
      heading: 'Three steps to launch',
      steps: [
        { id: newId('st'), title: 'Describe it', description: 'Tell us what you want to build.', icon: 'pencil' },
        { id: newId('st'), title: 'Review', description: 'Tweak the generated design to taste.', icon: 'eye' },
        { id: newId('st'), title: 'Publish', description: 'Go live on your own domain.', icon: 'rocket' },
      ],
    }
    case 'comparison_table':   return {
      id, type, stickyHeader: true,
      heading: 'Compare plans',
      columns: [
        { id: newId('cc'), label: 'Starter' },
        { id: newId('cc'), label: 'Pro', highlighted: true, badge: 'Most popular' },
        { id: newId('cc'), label: 'Enterprise' },
      ],
      rows: [
        { id: newId('cr'), label: 'Projects', values: ['1', '10', 'Unlimited'] },
        { id: newId('cr'), label: 'Custom domain', values: [false, true, true] },
        { id: newId('cr'), label: 'Priority support', values: [false, false, true] },
      ],
    }
    case 'social_links':       return {
      id, type, mode: 'follow', align: 'left', iconSize: 20, variant: 'circle',
      links: [
        { id: newId('so'), network: 'x', url: 'https://x.com/' },
        { id: newId('so'), network: 'linkedin', url: 'https://linkedin.com/' },
        { id: newId('so'), network: 'github', url: 'https://github.com/' },
      ],
    }
    case 'lottie':             return {
      id, type, url: '', height: 280, loop: true, autoplay: true, trigger: 'scroll',
    }
    case 'floating_button':    return {
      id, type, position: 'right-center', vertical: true, hideBelow: 640,
      button: { id: `${id}_btn`, label: 'Book a demo', variant: 'primary', size: 'md',
                on_click: { kind: 'external_url', url: '/contact/', newTab: false } },
    }
    case 'cookie_consent':     return {
      id, type,
      message: 'We use cookies to understand how the site is used and to improve it.',
      acceptLabel: 'Accept', declineLabel: 'Decline', settingsLabel: 'Preferences',
      position: 'bottom',
      storageKey: 'pg_consent',
      categories: [
        { id: 'necessary', label: 'Strictly necessary', required: true, description: 'Needed for the site to work.' },
        { id: 'analytics', label: 'Analytics', defaultOn: false, description: 'Helps us understand usage.' },
      ],
    }
  }
}

export function makeFilter(): EmbedFilter {
  return {
    id: newFilterId(),
    column: '',
    op: 'eq',
    value: { source: 'page_input', name: '' },
  }
}

export function makeArgMapping(target = ''): ArgMapping {
  return {
    id: newId('arg'),
    target,
    label: '',
    source: { source: 'page_input', name: '' },
  }
}

// ─── Slash menu / picker options ──────────────────────

export type BlockCategory = 'Basic' | 'Layout' | 'Content' | 'Media' | 'Action' | 'Form' | 'App' | 'eCommerce'

export interface BlockOption {
  type: BlockType
  label: string
  description: string
  icon: string
  category: BlockCategory
}

export const BLOCK_OPTIONS: BlockOption[] = [
  // Basic
  { type: 'paragraph',     label: 'Text',          description: 'Plain paragraph',                    icon: '¶',   category: 'Basic' },
  { type: 'heading',       label: 'Heading',       description: 'Section heading (H1/H2/H3)',         icon: 'H',   category: 'Basic' },
  { type: 'bullet_list',   label: 'Bulleted list', description: 'Unordered list item',                icon: '•',   category: 'Basic' },
  { type: 'numbered_list', label: 'Numbered list', description: 'Ordered list item',                  icon: '1.',  category: 'Basic' },
  { type: 'todo',          label: 'To-do',         description: 'Checkbox item',                      icon: '☐',   category: 'Basic' },
  { type: 'quote',         label: 'Quote',         description: 'Block quote',                        icon: '❝',   category: 'Basic' },
  { type: 'code',          label: 'Code',          description: 'Monospace code block',               icon: '</>', category: 'Basic' },
  { type: 'divider',       label: 'Divider',       description: 'Horizontal divider',                 icon: '—',   category: 'Basic' },
  { type: 'callout',       label: 'Callout',       description: 'Highlighted info box',               icon: '💡',  category: 'Basic' },

  // Layout
  { type: 'hero',          label: 'Hero',          description: 'Big banner with title + CTAs',       icon: '🎯',  category: 'Layout' },
  { type: 'cta_banner',    label: 'CTA banner',    description: 'Call-to-action banner section',      icon: '📣',  category: 'Layout' },
  { type: 'columns',       label: 'Columns',       description: 'Multi-column layout',                icon: '▥',   category: 'Layout' },
  { type: 'container',     label: 'Container',     description: 'Colored section container',          icon: '▢',   category: 'Layout' },
  { type: 'spacer',        label: 'Spacer',        description: 'Vertical spacing',                   icon: '↕',   category: 'Layout' },
  { type: 'card',          label: 'Card',          description: 'Single content card',                icon: '🃏',  category: 'Layout' },
  { type: 'card_grid',     label: 'Card grid',     description: 'Grid of cards',                      icon: '▦',   category: 'Layout' },

  // Content
  { type: 'feature_grid',  label: 'Feature grid',  description: 'Icon + title + description grid',    icon: '✨',  category: 'Content' },
  { type: 'stats',         label: 'Stats',         description: 'Number + label statistics',          icon: '#',   category: 'Content' },
  { type: 'countdown',     label: 'Countdown',     description: 'Live countdown to a target date/time', icon: '◴',  category: 'Content' },
  { type: 'timer',         label: 'Timer',         description: 'Live time elapsed since a start (ticket age, wait time)', icon: '⏱', category: 'Content' },
  { type: 'testimonial',   label: 'Testimonial',   description: 'Customer quote',                     icon: '❝',   category: 'Content' },
  { type: 'pricing',       label: 'Pricing table', description: 'Pricing tiers with CTAs',            icon: '$',   category: 'Content' },
  { type: 'faq',           label: 'FAQ',           description: 'Question/answer accordion',          icon: '❓',  category: 'Content' },
  { type: 'tabs',          label: 'Tabs',          description: 'Tabbed content',                     icon: '⊟',   category: 'Content' },
  { type: 'accordion',     label: 'Accordion',     description: 'Collapsible sections',               icon: '☰',   category: 'Content' },
  { type: 'timeline',      label: 'Timeline',      description: 'Vertical timeline of events / milestones', icon: '◷',  category: 'Content' },
  { type: 'logo_cloud',    label: 'Logo cloud',    description: 'Logo strip',                         icon: '⬢',   category: 'Content' },

  // Media
  { type: 'image',         label: 'Image',         description: 'Image from URL',                     icon: '🖼',  category: 'Media' },
  { type: 'video',         label: 'Video',         description: 'YouTube / Vimeo / file URL',         icon: '▶',   category: 'Media' },
  { type: 'gallery',       label: 'Gallery',       description: 'Image gallery',                      icon: '🗂',  category: 'Media' },
  { type: 'map',           label: 'Map',           description: 'Interactive map (Leaflet/Google/Mapbox)', icon: '📍',  category: 'Media' },

  // Action
  { type: 'button',        label: 'Button',        description: 'Single call-to-action button',       icon: '▣',   category: 'Action' },
  { type: 'button_group',  label: 'Button group',  description: 'Row of buttons',                     icon: '▥',   category: 'Action' },

  // Form
  { type: 'newsletter',    label: 'Newsletter',    description: 'Email signup with button',           icon: '✉',   category: 'Form' },
  { type: 'contact_form',  label: 'Contact form',  description: 'Name / email / message form',        icon: '📝',  category: 'Form' },

  // App
  { type: 'input_group',   label: 'Inputs',        description: 'Render page input controls',         icon: '⌨',   category: 'App' },
  { type: 'embed',         label: 'Embed object',  description: 'Embed a transaction, dashboard, visual, report, wizard, or another smart page', icon: '⊞',  category: 'App' },
  { type: 'file_upload',   label: 'File upload',    description: 'Attach a file or image, stored by document type + transaction id', icon: '📎', category: 'App' },

  // v2 additions
  { type: 'form',          label: 'Form',          description: 'Generic form with custom fields & submit action', icon: '🗒', category: 'Form' },
  { type: 'custom_code',   label: 'Custom code',   description: 'Sandboxed HTML / CSS / JS block',    icon: '⌘', category: 'App' },
  { type: 'custom_html',   label: 'Custom HTML',   description: 'Raw HTML block (sandboxed iframe; CSS/JS optional)', icon: '</>', category: 'App' },
  { type: 'nav_menu',      label: 'Nav menu',      description: 'Site navigation menu with multi-level dropdown submenus (folders → submenus)', icon: '🧭', category: 'App' },
  { type: 'cart_badge',    label: 'Cart badge',    description: 'Cart link with a live item count — for a site header', icon: '🛒', category: 'App' },
  { type: 'cart_lines',    label: 'Cart lines',    description: 'The basket: image, name, quantity stepper, line total, remove', icon: '🧾', category: 'App' },
  { type: 'cart_summary',  label: 'Cart summary',  description: 'Subtotal, declared tax and shipping, total, checkout button', icon: '∑', category: 'App' },
  { type: 'wishlist_button', label: 'Wishlist button', description: 'Heart that saves an item to the visitor\u2019s wishlist — works on a published page', icon: '♡', category: 'App' },
  { type: 'wishlist_badge',  label: 'Wishlist badge',  description: 'Wishlist link with a live count — for a site header', icon: '♥', category: 'App' },
  { type: 'wishlist_lines',  label: 'Wishlist lines',  description: 'The saved items: image, name, price, Move to cart, Remove', icon: '📋', category: 'App' },
  { type: 'filter_bar',    label: 'Filter bar',    description: 'Search, facets and sort over another block\u2019s items — works on a published page', icon: '⚲', category: 'App' },
  { type: 'breadcrumb',    label: 'Breadcrumb',    description: 'The trail to this page — links plus BreadcrumbList structured data', icon: '›', category: 'App' },
  { type: 'table',         label: 'Table',         description: 'Simple data table with headers and rows', icon: '⊞', category: 'Content' },
  { type: 'variable',      label: 'Variable',      description: 'Display the live value of a page variable', icon: '𝑥', category: 'App' },
  { type: 'notepad',       label: 'Notepad',       description: 'Rich-text notepad with formatting toolbar', icon: '📝', category: 'Basic' },
  { type: 'rich_content',  label: 'Rich content',  description: 'Show a Rich Text column from your data — an article body, a product description — with headings, images and a table of contents', icon: '📰', category: 'Content' },
  { type: 'collection_search', label: 'Collection search', description: 'Search box over a website collection — blog posts, products, jobs — searching titles, summaries and tags as the visitor types', icon: '🔎', category: 'Content' },
  { type: 'comments',      label: 'Comments',      description: 'Reader comments on a collection record (e.g. a blog post): the approved comments and a form, moderated in the Run view', icon: '💬', category: 'Content' },
  { type: 'chatbot',       label: 'Chatbot',       description: 'AI assistant grounded on the page content + project data', icon: '💬', category: 'App' },
  { type: 'auth_form',     label: 'Sign-in form',  description: 'Login / register form for a branded auth page', icon: '🔑', category: 'App' },
  { type: 'auth_menu',     label: 'Authentication', description: 'Header auth widget: Log in / Sign up buttons that become a user avatar + account menu when signed in', icon: '👤', category: 'App' },

  // Marketing sections (Tier 6). All are presentational and work on the
  // published static site — none of them need live data or a session.
  { type: 'bento_grid',       label: 'Bento grid',       description: 'Asymmetric tile grid — the modern product-page layout', icon: '▦', category: 'Layout' },
  { type: 'carousel',         label: 'Carousel',         description: 'Slide carousel with dots, arrows and autoplay', icon: '⟳', category: 'Layout' },
  { type: 'steps',            label: 'Steps / process',  description: 'Numbered how-it-works steps with connectors', icon: '⇉', category: 'Content' },
  { type: 'team',             label: 'Team',             description: 'Team member cards with photo, role and links', icon: '👥', category: 'Content' },
  { type: 'comparison_table', label: 'Comparison table', description: 'Feature matrix with check / cross cells', icon: '⊞', category: 'Content' },
  { type: 'counter',          label: 'Counter',          description: 'Numbers that count up when scrolled into view', icon: '#', category: 'Content' },
  { type: 'marquee',          label: 'Marquee',          description: 'Continuously scrolling strip of text or logos', icon: '↔', category: 'Content' },
  { type: 'before_after',     label: 'Before / after',   description: 'Draggable image comparison slider', icon: '◑', category: 'Media' },
  { type: 'lottie',           label: 'Animation',        description: 'Lottie / animated SVG with a poster fallback', icon: '✦', category: 'Media' },
  { type: 'social_links',     label: 'Social links',     description: 'Follow icons, or share-this-page buttons', icon: '⇗', category: 'Action' },
  { type: 'floating_button',  label: 'Floating button',  description: 'A call to action pinned to the screen edge or a corner — e.g. a "Book demo" side tab on every page', icon: '📌', category: 'Action' },
  { type: 'cookie_consent',   label: 'Cookie consent',   description: 'Consent banner that gates analytics until accepted', icon: '🍪', category: 'App' },

  // eCommerce — Proteus Vision integration
  { type: 'entity_picker',      label: 'Entity picker',       description: 'Search & select a customer, supplier, employee — any list endpoint or transaction',  icon: '🔎', category: 'eCommerce' },
  { type: 'new_customer_form',  label: 'New customer form',   description: 'Capture and create a new customer in Vision ERP',                 icon: '👤', category: 'eCommerce' },
  { type: 'product_selection',  label: 'Product selection',   description: 'Pick product → style → preferences → measurements; add to cart', icon: '🛍', category: 'eCommerce' },
  { type: 'product_preview',    label: 'Product preview',     description: 'Live, attribute-driven product preview (works for shirts, cars, furniture)', icon: '🪞', category: 'eCommerce' },
  { type: 'order_summary',      label: 'Order summary',       description: 'Cart of captured items with edit / remove and totals',            icon: '🛒', category: 'eCommerce' },
  { type: 'order_submit',       label: 'Order submit',        description: 'Create the sales order, capture id + order number on the doc',   icon: '✓',  category: 'eCommerce' },
  { type: 'payment_checkout',   label: 'Payment checkout',    description: 'Take payment for a saved record through this project\u2019s own gateway', icon: '💳', category: 'eCommerce' },
  { type: 'cart_checkout',      label: 'Checkout',            description: 'Contact + delivery address (saved addresses for a signed-in shopper) and an order summary; saves the basket as an order through the application\u2019s own logic', icon: '🧾', category: 'eCommerce' },
  { type: 'my_records',         label: 'My records',          description: 'The signed-in user\u2019s own records — orders, bookings, applications, tickets — with status chips and lines', icon: '📦', category: 'eCommerce' },
  { type: 'reviews',            label: 'Reviews',             description: 'Approved reviews of any record (a product, a doctor, a course) with a star summary, and a moderated form', icon: '⭐', category: 'eCommerce' },
  { type: 'payment_settings',   label: 'Payment settings',    description: 'Let this application’s administrator connect the gateway it charges on', icon: '🔐', category: 'eCommerce' },
]

// Block-level text accessor (returns null for blocks without a single
// inline text field). Todo, bullet_list, and numbered_list are list
// blocks — their text lives on the per-item editors, so we return null
// here and the BlockNode list editors handle keyboard / DOM sync.
export function getBlockText(b: Block): string | null {
  switch (b.type) {
    case 'heading':
    case 'paragraph':
    case 'quote':
    case 'code':
      return b.text
    default:
      return null
  }
}

export function setBlockText(b: Block, text: string): Block {
  switch (b.type) {
    case 'heading':
    case 'paragraph':
    case 'quote':
    case 'code':
      return { ...b, text }
    default:
      return b
  }
}
